import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  FormSelect,
  FormSelectOption,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet } from "../../app/api";
import { navigateWithFallback } from "../../app/legacyBridge";
import type { InventoryMovementPayload, InventoryPayload, InventoryRow, Location, LocationsPayload, User, ViewKey } from "../../types";
import {
  canAddWarehouseInventory,
  canUseInventoryCount,
  canUseWasteLoss,
  formatNumber,
  formatQuantity,
  inventoryItemLabel,
  inventoryLocationLabel,
  locationLabel
} from "../../display";

export function InventoryActionsWorkspace({ user }: { user: User }) {
  const defaultScope = user.role === "Purchaser" || user.role === "Warehouse SL" ? "warehouse" : "all";
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [scope, setScope] = useState(defaultScope);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);
  const [movement, setMovement] = useState<InventoryMovementPayload | null>(null);
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementLoading, setMovementLoading] = useState(false);

  const visibleLocations = useMemo(() => {
    if (scope === "warehouse") {
      return locations.filter(location => ["COMD", "COMW"].includes(String(location.code || "").toUpperCase()));
    }
    if (scope === "branch") {
      return locations.filter(location => String(location.location_type || "").toLowerCase() === "branch");
    }
    return locations;
  }, [locations, scope]);

  const totals = useMemo(() => {
    const itemCount = new Set(rows.map(row => row.product_id)).size;
    const locationCount = new Set(rows.map(row => row.location_id)).size;
    const totalQty = rows.reduce((sum, row) => sum + Number(row.qty_on_hand || 0), 0);
    return { itemCount, locationCount, totalQty };
  }, [rows]);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const params = new URLSearchParams();
      if (scope !== "all") params.set("scope", scope);
      if (selectedLocation) params.set("location_id", selectedLocation);
      if (search.trim()) params.set("q", search.trim());
      const payload = await apiGet<InventoryPayload>(`/api/inventory?${params.toString()}`);
      setRows(payload.inventory || []);
    } catch (err) {
      setRows([]);
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, [scope, search, selectedLocation]);

  const loadLocations = useCallback(async () => {
    try {
      const payload = await apiGet<LocationsPayload>("/api/locations");
      setLocations(payload.locations || []);
    } catch (err) {
      setLocations([]);
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Locations could not be loaded." });
    }
  }, []);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    setSelectedLocation(current => visibleLocations.some(location => String(location.id) === current) ? current : "");
  }, [visibleLocations]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadInventory();
    }, 200);
    return () => window.clearTimeout(timer);
  }, [loadInventory]);

  const openMovement = useCallback(async (row: InventoryRow) => {
    setMovementOpen(true);
    setMovementLoading(true);
    setMovement(null);
    try {
      const params = new URLSearchParams({
        location_id: String(row.location_id),
        product_id: String(row.product_id)
      });
      setMovement(await apiGet<InventoryMovementPayload>(`/api/inventory/movements?${params.toString()}`));
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Movement history could not be loaded." });
      setMovementOpen(false);
    } finally {
      setMovementLoading(false);
    }
  }, []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-inventory-actions-view" className="pf-toj-inventory-actions">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
          <h1>Inventory Actions</h1>
            <p>Check stock on hand, open movement history, enter counts, or report waste and loss from one place.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading Inventory Actions" />
            </FlexItem>
          )}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <Card className="pf-toj-work-card pf-toj-inventory-control-card">
          <CardBody>
            <Toolbar className="pf-toj-inventory-toolbar">
              <ToolbarContent>
                <ToolbarItem className="pf-toj-search-toolbar-item">
                  <TextInput
                    aria-label="Search inventory"
                    placeholder="Search item, SKU, location, type, or unit"
                    value={search}
                    onChange={(_event, value) => setSearch(String(value))}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <FormSelect aria-label="Inventory scope" value={scope} onChange={(_event, value) => setScope(String(value))}>
                    <FormSelectOption value="all" label="All visible stock" />
                    <FormSelectOption value="warehouse" label="Wet and dry goods" />
                    <FormSelectOption value="branch" label="Branch stock" />
                  </FormSelect>
                </ToolbarItem>
                <ToolbarItem>
                  <FormSelect aria-label="Inventory location" value={selectedLocation} onChange={(_event, value) => setSelectedLocation(String(value))}>
                    <FormSelectOption value="" label="All locations in this view" />
                    {visibleLocations.map(location => (
                      <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />
                    ))}
                  </FormSelect>
                </ToolbarItem>
                <ToolbarItem>
                  <Button variant="secondary" onClick={() => void loadInventory()}>Refresh</Button>
                </ToolbarItem>
              </ToolbarContent>
            </Toolbar>
            <div className="pf-toj-inventory-action-strip" role="group" aria-label="Inventory actions">
              {canUseInventoryCount(user) && (
                <Button variant="primary" onClick={() => void navigateWithFallback("store_count")}>Enter inventory count</Button>
              )}
              {canUseWasteLoss(user) && (
                <Button variant="secondary" onClick={() => void navigateWithFallback("manual_adjustments" as ViewKey)}>Report waste or loss</Button>
              )}
              {canAddWarehouseInventory(user) && (
                <Button variant="secondary" onClick={() => void navigateWithFallback("add_inventory")}>Add to inventory</Button>
              )}
            </div>
            <div className="pf-toj-inventory-summary" aria-label="Inventory summary">
              <span><strong>{formatNumber(totals.itemCount)}</strong> items shown</span>
              <span><strong>{formatNumber(totals.locationCount)}</strong> locations</span>
              <span><strong>{formatQuantity(totals.totalQty)}</strong> total quantity</span>
            </div>
            <div className="pf-toj-table-wrap">
              <Table aria-label="Inventory stock on hand" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Location</Th>
                    <Th>Type</Th>
                    <Th>Unit</Th>
                    <Th textCenter>Quantity</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.slice(0, 120).map(row => (
                    <Tr key={`${row.location_id}-${row.product_id}`}>
                      <Td dataLabel="Item">
                        <strong>{inventoryItemLabel(row)}</strong>
                        <small>{row.sku || "No SKU"}</small>
                      </Td>
                      <Td dataLabel="Location">{inventoryLocationLabel(row)}</Td>
                      <Td dataLabel="Type">{row.product_type || row.category || "Not set"}</Td>
                      <Td dataLabel="Unit">{row.uom || "Not set"}</Td>
                      <Td dataLabel="Quantity" textCenter>{formatQuantity(row.qty_on_hand)}</Td>
                      <Td dataLabel="Action">
                        <Button variant="link" isInline onClick={() => void openMovement(row)}>Movement history</Button>
                      </Td>
                    </Tr>
                  ))}
                  {!rows.length && !loading && (
                    <Tr>
                      <Td colSpan={6}>
                        <div className="pf-toj-empty">
                          <strong>No stock matches this view.</strong>
                          <span>Try another location or search term.</span>
                        </div>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
              {rows.length > 120 && (
                <p className="pf-toj-muted pf-toj-row-limit-note">Showing the first 120 rows. Use search or a location filter to narrow the list.</p>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
      <Modal variant={ModalVariant.large} isOpen={movementOpen} onClose={() => setMovementOpen(false)}>
        <ModalHeader title="Movement history" />
        <ModalBody>
          {movementLoading && <Spinner size="md" aria-label="Loading movement history" />}
          {movement && (
            <div className="pf-toj-movement-body">
              <div>
                <strong>{movement.item?.product_name || "Inventory item"}</strong>
                <p>{[movement.location?.location_code, movement.location?.location_name].filter(Boolean).join(" - ")}</p>
              </div>
              <div className="pf-toj-inventory-summary">
                {(movement.summary || []).map(row => (
                  <span key={row.key || row.label}><strong>{formatQuantity(row.quantity)}</strong> {row.label || "Movement"} ({formatNumber(row.count)})</span>
                ))}
              </div>
              <Table aria-label="Movement history detail" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Reference</Th>
                    <Th textCenter>Quantity</Th>
                    <Th>Notes</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(movement.details || []).slice(0, 80).map((row, index) => (
                    <Tr key={`${row.created_at}-${row.reference}-${index}`}>
                      <Td dataLabel="Date">{row.created_at || ""}</Td>
                      <Td dataLabel="Type">{row.category_label || row.movement_type || ""}</Td>
                      <Td dataLabel="Reference">{[row.reference, row.parent_bor].filter(Boolean).join(" / ")}</Td>
                      <Td dataLabel="Quantity" textCenter>{formatQuantity(row.quantity)}</Td>
                      <Td dataLabel="Notes">{row.notes || row.created_by_name || ""}</Td>
                    </Tr>
                  ))}
                  {!(movement.details || []).length && (
                    <Tr>
                      <Td colSpan={5}>No movement history was found for this item and location.</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setMovementOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </PageSection>
  );
}
